import type { Session } from '@supabase/supabase-js';
import { isAuthConfigured, supabase } from './supabase';

/**
 * Identity, without a sign-in screen.
 *
 * The game asks nothing of a player before letting them in: the first launch
 * mints an anonymous Supabase user and that is the account from then on. The API
 * is unchanged and still verifies a real JWT on every request — an anonymous
 * user is a normal authenticated user to it, so the pet stays server-owned and
 * keeps decaying while the app is closed.
 *
 * The trade this makes is deliberate: the pet is tied to whatever holds the
 * session (this device's storage), so clearing app data starts a new life. That
 * is the price of no sign-in, and `linkEmail`-style upgrades are the way back if
 * accounts are ever wanted again.
 */

/**
 * In flight or finished bootstrap.
 *
 * Without this, two callers racing on first launch (a screen and a refetch, say)
 * would each see "no session" and create a *separate* anonymous user — the
 * second one silently orphaning the first one's pet.
 */
let bootstrap: Promise<Session> | null = null;

async function createSession(): Promise<Session> {
  // Without this the failure is a confusing 401 from a placeholder Supabase URL.
  // A build shipped without credentials is a deploy mistake, and should say so.
  if (!isAuthConfigured) {
    throw new Error(
      'This build has no Supabase credentials: set VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY and rebuild.',
    );
  }

  const { data: existing } = await supabase.auth.getSession();
  if (existing.session) return existing.session;

  const { data, error } = await supabase.auth.signInAnonymously();
  if (error) throw new Error(error.message);
  if (!data.session)
    throw new Error('Supabase accepted the anonymous sign-in but returned no session.');
  return data.session;
}

/** The current session, creating an anonymous one on first launch. */
export function ensureSession(): Promise<Session> {
  if (!bootstrap) {
    bootstrap = createSession().catch((error: unknown) => {
      // A failed attempt must not be cached, or the app can never recover from
      // a transient network error without a restart.
      bootstrap = null;
      throw error;
    });
  }
  return bootstrap;
}

/**
 * Abandons this identity and starts a fresh one.
 *
 * The pet belongs to the old anonymous user, so this is effectively "new game" —
 * which is exactly why it lives behind a confirmation in the UI.
 */
export async function resetIdentity(): Promise<Session> {
  bootstrap = null;
  await supabase.auth.signOut();
  return ensureSession();
}

/**
 * The current access token, refreshed if it is close to expiring.
 *
 * `getSession` handles the refresh itself, so callers get a usable token or
 * `null` — never a stale one.
 */
export async function getAccessToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

/** Fires immediately with the current session, then on every change. */
export function onSessionChange(handler: (session: Session | null) => void): () => void {
  void supabase.auth.getSession().then(({ data }) => handler(data.session));
  const { data } = supabase.auth.onAuthStateChange((_event, session) => handler(session));
  return () => data.subscription.unsubscribe();
}
